import React, { useState } from 'react';
import {
  ExternalLink, Smartphone, Search, Package, Store, Sparkles,
  Globe, Heart, ShoppingCart, FileEdit, Edit3, ClipboardCheck,
  HelpCircle, Headset, ArrowLeft
} from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';
import { buildSearchableText, matchesAllTerms } from 'core/utils/search';
import SavedCartsPanel from './cart/SavedCartsPanel';
import ProcurementKpiDashboard from './ProcurementKpiDashboard';

const CatalogView = ({
  catalogs,
  user,
  onSelectCatalog,
  searchTerm,
  onSearch,
  ociMode,
  ociParams,
  onBack,
  // Neue Props
  cart,
  getCartTotal,
  drafts,
  purchaseRequests,
  onOpenCart,
  onNewBanf,
  onOpenDraft,
  onOpenRequests,
  // Gespeicherte Warenkörbe
  savedCarts,
  onLoadSavedCart,
  onRenameSavedCart,
  onDeleteSavedCart,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // Typ-Filter lokaler State
  const [selectedType, setSelectedType] = useState('all');

  // Mobile Detection
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  };

  // Defensives Lesen neuer Props
  const safeCart = Array.isArray(cart) ? cart : [];
  const safeDrafts = Array.isArray(drafts) ? drafts : [];
  const cartTotal = typeof getCartTotal === 'function' ? (getCartTotal() || 0) : 0;
  const safeRequests = Array.isArray(purchaseRequests) ? purchaseRequests : [];
  const pendingApprovals = safeRequests.filter(r => r && r.status === 'pending').length;

  // Hilfsfunktion: Wert eines Entwurfs defensiv lesen
  const getDraftValue = (draft) => {
    const v = draft.total ?? draft.value ?? 0;
    return typeof v === 'number' ? v : 0;
  };

  // Hilfsfunktion: Positionsanzahl eines Entwurfs defensiv lesen
  const getDraftPositions = (draft) => {
    const items = draft.items ?? draft.positions ?? [];
    return Array.isArray(items) ? items.length : 0;
  };

  // Hilfsfunktion: Name/Titel eines Entwurfs defensiv lesen
  const getDraftTitle = (draft) => {
    return draft.title || draft.name || `Entwurf #${draft.id}`;
  };

  // EUR-Formatierung
  const formatEur = (val) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(val || 0);

  // Create Favoriten catalog
  const favoritenCatalog = {
    id: 'favoriten',
    name: t('catalogs.favorites'),
    supplier: t('catalogs.favoritesSupplier'),
    description: t('catalogs.favoritesDesc'),
    type: 'internal',
    icon: '⭐',
    category: 'Persönlich'
  };

  // Combine Favoriten with regular catalogs
  const allCatalogs = [favoritenCatalog, ...catalogs];

  // Typ-Filter + Suchfilter kombinieren
  const filteredCatalogs = allCatalogs.filter(catalog => {
    // Typ-Filter
    if (selectedType === 'oci') {
      if (catalog.type !== 'oci') return false;
    } else if (selectedType === 'internal') {
      if (!(catalog.type === 'internal' && catalog.id !== 'favoriten')) return false;
    } else if (selectedType === 'favorites') {
      if (catalog.id !== 'favoriten') return false;
    }
    // Suchfilter
    return matchesAllTerms(
      buildSearchableText(catalog.name, catalog.supplier, catalog.description, catalog.category, catalog.type),
      searchTerm
    );
  });

  // Typ-Filter-Chips
  const filterChips = [
    { key: 'all', label: t('catalogs.filterAll') },
    { key: 'oci', label: t('catalogs.filterOci') },
    { key: 'internal', label: t('catalogs.filterInternal') },
    { key: 'favorites', label: t('catalogs.filterFavorites') },
    { key: 'kpis', label: t('catalogs.filterKpis') },
  ];

  return (
    <div className={`flex-1 ${isDarkMode
      ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800'
      : 'bg-slate-50'
    }`}>

      {/* ── Header ── */}
      <div className="relative">
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gray-900/20 border-b border-white/10 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
          : 'bg-white border-b border-slate-200'
        }`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-r from-blue-900/5 via-transparent to-blue-900/5'
          : ''
        }`}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex items-start justify-between gap-4 mb-1">
            <div className="flex items-center space-x-3">
            {/* Zurück (nicht im OCI-Punch-out, dort gibt es kein Home) */}
            {!ociMode && typeof onBack === 'function' && (
              <button
                onClick={onBack}
                className={`p-2 rounded-xl transition-colors ${isDarkMode
                  ? 'hover:bg-white/10 text-white/70 hover:text-white'
                  : 'hover:bg-slate-100 text-slate-500 hover:text-slate-700'}`}
                aria-label={t('common.back')}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            {/* Icon */}
            <div className="relative">
              <div className={`absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-xl blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
              <div className={`relative ${isDarkMode
                ? 'bg-gradient-to-br from-slate-600/60 via-gray-600/50 to-slate-700/60 backdrop-blur-xl shadow-[0_6px_24px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                : 'bg-brand/10'
              } w-10 h-10 rounded-xl flex items-center justify-center`}>
                <Package className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
              </div>
            </div>
            <div>
              <h1 className={`text-2xl font-semibold ${isDarkMode
                ? 'bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent drop-shadow-lg'
                : 'text-slate-900'
              }`}>{t('catalogs.title')}</h1>
              <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
                {t('catalogs.subtitle')}
              </p>
            </div>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <button
                onClick={() => typeof onNewBanf === 'function' && onNewBanf()}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${isDarkMode
                  ? 'bg-brand/20 hover:bg-brand/30 text-blue-200 border border-brand/30'
                  : 'bg-brand hover:bg-[#1D4ED8] text-white'}`}
              >
                <Edit3 className="w-4 h-4" /> {t('catalogs.blankBanfBtn')}
              </button>
            </div>
          </div>

          {/* Typ-Filter-Chips */}
          <div className="flex flex-wrap gap-2 mt-4">
            {filterChips.map(chip => (
              <button
                key={chip.key}
                onClick={() => setSelectedType(chip.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedType === chip.key
                    ? 'bg-brand text-white shadow-sm'
                    : isDarkMode
                      ? 'bg-white/10 border border-white/20 text-white/70 hover:bg-white/15'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-brand/40 hover:text-brand'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* ── OCI-Modus-Hinweis (kompakt) ── */}
        {ociMode && ociParams && (
          <div className={`mb-4 flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm ${isDarkMode
            ? 'bg-brand/15 border border-brand/30 text-blue-200'
            : 'bg-brand/5 border border-brand/20 text-brand'
          }`}>
            <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse flex-shrink-0"></div>
            <span className="font-medium">{t('catalogs.ociModeActive')}</span>
            <span className={`${isDarkMode ? 'text-white/40' : 'text-brand/40'}`}>·</span>
            <span className={`${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('catalogs.ociModeDesc')}</span>
          </div>
        )}

        {/* ── Mobil-Warnung (kompakt) ── */}
        {isMobile() && (
          <div className={`mb-4 flex items-start gap-3 px-4 py-2.5 rounded-xl text-sm ${isDarkMode
            ? 'bg-amber-500/10 border border-amber-400/25 text-amber-200'
            : 'bg-amber-50 border border-amber-200 text-amber-800'
          }`}>
            <Smartphone className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{t('catalogs.mobileWarning')} — {t('catalogs.mobileWarningDetail')}</span>
          </div>
        )}

        {/* ── Suchergebnis-Hinweis ── */}
        {searchTerm && (
          <div className="mb-4">
            <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
              <span className={`font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-900'}`}>{filteredCatalogs.length}</span>
              {' '}von{' '}
              <span className={`font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-900'}`}>{allCatalogs.length}</span>
              {' '}Katalogen gefunden für „{searchTerm}"
            </p>
          </div>
        )}

        {/* ── Status-Streifen (echte Daten · Absprung) ── */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { icon: FileEdit, label: t('catalogs.statusDrafts'), value: safeDrafts.length, onClick: () => typeof onOpenRequests === 'function' && onOpenRequests('drafts') }, // -> BANF-Liste, Tab Entwürfe (Taras 28.07.; vorher: öffnete ersten Entwurf direkt)
            { icon: ShoppingCart, label: t('catalogs.statusInCart'), value: safeCart.length, onClick: () => typeof onOpenCart === 'function' && onOpenCart() },
            { icon: ClipboardCheck, label: t('catalogs.statusApproval'), value: pendingApprovals, onClick: () => typeof onOpenRequests === 'function' && onOpenRequests() },
          ].map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <button
                key={i}
                onClick={kpi.onClick}
                className={`flex items-center gap-3 p-4 rounded-2xl text-left transition-all duration-200 ${isDarkMode
                  ? 'bg-gray-900/40 border border-white/15 hover:border-brand/40 backdrop-blur-xl'
                  : 'glass-light-card border border-slate-200 hover:border-brand/40'}`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-brand/20' : 'bg-brand/10'}`}>
                  <Icon className={`w-4 h-4 ${isDarkMode ? 'text-blue-300' : 'text-brand'}`} />
                </div>
                <div className="min-w-0">
                  <div className={`text-xl font-bold leading-none ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{kpi.value}</div>
                  <div className={`text-xs mt-1 truncate ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{kpi.label}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* ── KPI-Dashboard (Reiter „Kennzahlen") ── */}
        {selectedType === 'kpis' && (
          <ProcurementKpiDashboard purchaseRequests={safeRequests} onOpenRequests={onOpenRequests} user={user} />
        )}

        {/* ── 12-Spalten-Haupt-Grid (außer im KPI-Reiter) ── */}
        {selectedType !== 'kpis' && (
        <>

        {/* Gespeicherte Warenkörbe — oben beim Status-Streifen (Taras 29.07.), volle Breite, eingeklappt */}
        {Array.isArray(savedCarts) && savedCarts.length > 0 && (
          <div className="mb-6">
            <SavedCartsPanel
              savedCarts={savedCarts}
              onLoad={(sc, mode) => {
                if (typeof onLoadSavedCart === 'function') onLoadSavedCart(sc, mode);
              }}
              onRename={typeof onRenameSavedCart === 'function' ? onRenameSavedCart : () => {}}
              onDelete={typeof onDeleteSavedCart === 'function' ? onDeleteSavedCart : () => {}}
              collapsible={true}
              defaultOpen={false}
            />
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-6">

          {/* ── LINKS: Katalogkarten (8 Spalten) ── */}
          <div className="lg:col-span-8">

            {filteredCatalogs.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {filteredCatalogs.map(catalog => (
                  <div
                    key={catalog.id}
                    onClick={() => onSelectCatalog(catalog)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectCatalog(catalog); } }}
                    role="button"
                    tabIndex={0}
                    className="relative group h-full flex flex-col cursor-pointer"
                  >
                    {/* Card Background */}
                    <div className={`absolute inset-0 ${
                      catalog.id === 'favoriten'
                        ? isDarkMode
                          ? 'bg-gradient-to-br from-amber-900/40 via-yellow-800/30 to-amber-900/40 border border-amber-400/30 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
                          : 'glass-light-card border border-amber-200'
                        : isDarkMode
                          ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
                          : 'glass-light-card border border-slate-200'
                    } rounded-3xl`}></div>
                    <div className={`absolute inset-0 ${
                      catalog.id === 'favoriten'
                        ? isDarkMode ? 'bg-gradient-to-t from-amber-600/10 via-transparent to-yellow-600/10' : ''
                        : isDarkMode ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10' : ''
                    } rounded-3xl`}></div>

                    {/* Hover Glass Effect – nur Dark */}
                    <div className={`absolute inset-0 ${isDarkMode
                      ? 'bg-gradient-to-br from-white/0 via-white/0 to-white/0 group-hover:from-white/5 group-hover:via-white/10 group-hover:to-white/5'
                      : ''
                    } rounded-3xl transition-all duration-500`}></div>

                    {/* Card Content */}
                    <div className="relative p-6 h-full flex flex-col">
                      {/* Header */}
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          {/* Icon Container */}
                          <div className="relative group/icon">
                            <div className={`absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                            <div className={`relative w-12 h-12 rounded-2xl flex items-center justify-center ${isDarkMode
                              ? `backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20 ${
                                  catalog.id === 'favoriten'
                                    ? 'bg-gradient-to-br from-amber-600/60 via-yellow-600/50 to-amber-700/60'
                                    : catalog.type === 'oci'
                                      ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60'
                                      : 'bg-gradient-to-br from-emerald-600/60 via-emerald-700/50 to-emerald-800/60'
                                }`
                              : catalog.id === 'favoriten'
                                ? 'bg-amber-100'
                                : 'bg-brand/10'
                            }`}>
                              {catalog.icon && catalog.type === 'oci' ? (
                                <img
                                  src={catalog.icon}
                                  alt={`${catalog.name} favicon`}
                                  className="w-8 h-8 rounded object-contain relative z-10"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'block';
                                  }}
                                />
                              ) : null}
                              <div style={{ display: catalog.icon && catalog.type === 'oci' ? 'none' : 'block' }} className="relative z-10">
                                {catalog.icon && catalog.type === 'internal' ? (
                                  <span className="text-2xl">{catalog.icon}</span>
                                ) : catalog.type === 'oci' ? (
                                  <Globe className={`w-6 h-6 ${isDarkMode ? 'text-white/90' : 'text-brand'}`} />
                                ) : (
                                  <Store className={`w-6 h-6 ${isDarkMode ? 'text-white/90' : 'text-brand'}`} />
                                )}
                              </div>
                              <div className={`absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm group-hover/icon:scale-110 transition-transform duration-300 ${isDarkMode ? '' : 'hidden'}`}></div>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} truncate`}>{catalog.name}</h3>
                            <p className={`text-sm ${isDarkMode ? 'text-white/60 drop-shadow-sm' : 'text-slate-500'}`}>{t('products.supplierBy', { supplier: catalog.supplier })}</p>
                          </div>
                        </div>

                        {/* Type Badge */}
                        <div className={`relative inline-flex items-center px-3 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
                          catalog.id === 'favoriten'
                            ? isDarkMode
                              ? 'bg-amber-500/20 border border-amber-400/30 text-amber-200 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]'
                              : 'bg-amber-100 border border-amber-200 text-amber-700'
                            : catalog.type === 'oci'
                              ? isDarkMode
                                ? 'bg-blue-500/20 border border-blue-400/30 text-blue-200 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]'
                                : 'bg-brand/10 border border-brand/20 text-brand'
                              : isDarkMode
                                ? 'bg-emerald-500/20 border border-emerald-400/30 text-emerald-200 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]'
                                : 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                        }`}>
                          {catalog.id === 'favoriten' ? (
                            <><Heart className="w-3 h-3 mr-1" />{t('catalogs.favorites')}</>
                          ) : catalog.type === 'oci' ? (
                            <><ExternalLink className="w-3 h-3 mr-1" />{t('catalogs.ociShop')}</>
                          ) : (
                            <><Sparkles className="w-3 h-3 mr-1" />{t('catalogs.internalShop')}</>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      <p className={`text-sm ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-500'} mb-4 line-clamp-2`}>{catalog.description}</p>

                      {/* Footer Info */}
                      <div className="flex items-center justify-between mb-4">
                        <div className={`flex items-center text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>
                          {catalog.id === 'favoriten' ? (
                            <><Heart className="w-3 h-3 mr-1" />{t('catalogs.footerPersonal')}</>
                          ) : catalog.type === 'oci' && isMobile() ? (
                            <><Smartphone className="w-3 h-3 mr-1" />{t('catalogs.footerBrowser')}</>
                          ) : catalog.type === 'oci' ? (
                            <><ExternalLink className="w-3 h-3 mr-1" />{t('catalogs.footerNewWindow')}</>
                          ) : (
                            <><Store className="w-3 h-3 mr-1" />{t('catalogs.footerInternal')}</>
                          )}
                        </div>

                        {catalog.category && (
                          <span className={`text-xs ${isDarkMode
                            ? 'text-white/60 bg-white/10 border border-white/20 backdrop-blur-sm'
                            : 'text-slate-500 bg-slate-100 border border-slate-200'
                          } px-2 py-1 rounded-full`}>
                            {catalog.category}
                          </span>
                        )}
                      </div>

                      {/* Action Button */}
                      <button
                        onClick={(e) => { e.stopPropagation(); onSelectCatalog(catalog); }}
                        className={`relative mt-auto w-full group/button overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${
                          isDarkMode
                            ? ''
                            : catalog.id === 'favoriten'
                              ? 'bg-amber-500 hover:bg-amber-600'
                              : catalog.type === 'oci'
                                ? 'bg-brand hover:bg-[#1D4ED8]'
                                : 'bg-emerald-600 hover:bg-emerald-700'
                        }`}
                        style={isDarkMode ? {
                          background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
                          backdropFilter: 'blur(20px)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                        } : undefined}
                      >
                        {/* Hover Overlay – nur Dark */}
                        <div
                          className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/button:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                          style={{
                            background: isDarkMode
                              ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                              : undefined
                          }}
                        ></div>

                        {/* Button Content */}
                        <div className={`relative z-10 py-3 px-4 flex items-center justify-center font-medium transition-all duration-300 ${
                          isDarkMode
                            ? 'text-white drop-shadow-lg group-hover/button:drop-shadow-xl'
                            : 'text-white'
                        }`}>
                          {catalog.id === 'favoriten' ? (
                            <><Heart className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/button:scale-110" />{t('catalogs.openFavorites')}</>
                          ) : catalog.type === 'oci' ? (
                            <><ExternalLink className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/button:translate-x-0.5" />{t('catalogs.openShop')}</>
                          ) : (
                            <><Package className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/button:scale-110" />{t('catalogs.viewProducts')}</>
                          )}
                        </div>

                        {/* Wave Effect – nur Dark */}
                        <div className={`absolute inset-0 opacity-0 group-hover/button:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                          <div
                            className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/button:translate-x-full transition-transform duration-800"
                            style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)' }}
                          ></div>
                        </div>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* ── Empty State ── */
              <div className="relative group">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
                  : 'glass-light-card border border-slate-200'
                } rounded-3xl`}></div>
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10' : ''} rounded-3xl`}></div>

                <div className="relative p-12">
                  <div className="text-center">
                    <div className="relative mx-auto mb-4">
                      <div className={`absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-3xl blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                      <div className={`relative ${isDarkMode
                        ? 'bg-gradient-to-br from-slate-600/60 via-gray-600/50 to-slate-700/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20'
                        : 'bg-slate-100'
                      } w-16 h-16 rounded-3xl flex items-center justify-center`}>
                        <Search className={`w-8 h-8 ${isDarkMode ? 'text-white/80 drop-shadow-lg' : 'text-slate-400'}`} />
                        <div className={`absolute top-2 left-2 w-4 h-4 bg-white/30 rounded-full blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
                      </div>
                    </div>
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} mb-2`}>
                      {t('catalogs.empty')}
                    </h3>
                    <p className={`${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-500'} mb-6 max-w-sm mx-auto`}>
                      {t('catalogs.emptyDesc')}
                    </p>
                    {searchTerm && (
                      <button
                        onClick={() => onSearch('')}
                        className={`relative group/reset overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${
                          isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 font-medium'
                        }`}
                      >
                        <div className={`absolute inset-0 bg-gradient-to-r from-slate-600/60 via-gray-600/60 to-slate-700/60 backdrop-blur-xl ${isDarkMode ? '' : 'hidden'}`}></div>
                        <div className={`absolute inset-0 bg-gradient-to-t from-white/15 to-transparent ${isDarkMode ? '' : 'hidden'}`}></div>
                        <div className={`absolute inset-0 border border-white/30 rounded-2xl ${isDarkMode ? '' : 'hidden'}`}></div>
                        <div className={`absolute top-1 left-1 right-1 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent group-hover/reset:via-white/80 transition-all duration-300 ${isDarkMode ? '' : 'hidden'}`}></div>
                        <div className={`relative z-10 px-4 py-2 font-medium ${isDarkMode ? 'text-white drop-shadow-lg' : ''}`}>
                          {t('catalogs.resetSearch')}
                        </div>
                        <div className={`absolute inset-0 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] group-hover/reset:shadow-[0_12px_40px_rgba(0,0,0,0.6)] transition-all duration-500 ${isDarkMode ? '' : 'hidden'}`}></div>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Leere BANF: jetzt als Button oben im Header */}
          </div>

          {/* ── RECHTS: Sidebar (4 Spalten, sticky) ── */}
          <div className="lg:col-span-4 space-y-4 lg:sticky lg:top-4 lg:self-start">

            {/* a) Warenkorb-Karte */}
            <div className={`rounded-2xl overflow-hidden ${isDarkMode
              ? 'bg-gradient-to-br from-gray-900/40 via-gray-800/30 to-gray-900/40 border border-white/15 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
              : 'glass-light-card border border-slate-200'
            }`}>
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-brand/20' : 'bg-brand/10'}`}>
                      <ShoppingCart className={`w-4 h-4 ${isDarkMode ? 'text-blue-300' : 'text-brand'}`} />
                    </div>
                    <span className={`font-semibold text-sm ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>
                      {t('cart.title')}
                    </span>
                  </div>
                  {safeCart.length > 0 && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isDarkMode
                      ? 'bg-brand/20 text-blue-200 border border-brand/30'
                      : 'bg-brand/10 text-brand border border-brand/20'
                    }`}>
                      {safeCart.length} {t('catalogs.inCart')}
                    </span>
                  )}
                </div>

                <div className={`mb-3 pb-3 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
                  <div className="flex items-baseline justify-between">
                    <span className={`text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>{t('catalogs.estimatedTotal')}</span>
                    <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-brand'}`}>{formatEur(cartTotal)}</span>
                  </div>
                  <p className={`text-[11px] mt-1 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('catalogs.estimatedHint')}</p>
                </div>

                <button
                  onClick={() => typeof onOpenCart === 'function' && onOpenCart()}
                  className={`w-full py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${isDarkMode
                    ? 'bg-brand/20 hover:bg-brand/30 text-blue-200 border border-brand/30'
                    : 'bg-brand hover:bg-[#1D4ED8] text-white'
                  }`}
                >
                  {t('catalogs.goToCart')}
                </button>
              </div>
            </div>

            {/* b) Letzte Entwürfe */}
            <div className={`rounded-2xl overflow-hidden ${isDarkMode
              ? 'bg-gradient-to-br from-gray-900/40 via-gray-800/30 to-gray-900/40 border border-white/15 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
              : 'glass-light-card border border-slate-200'
            }`}>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-white/10' : 'bg-slate-100'}`}>
                    <FileEdit className={`w-4 h-4 ${isDarkMode ? 'text-white/70' : 'text-slate-500'}`} />
                  </div>
                  <span className={`font-semibold text-sm ${isDarkMode ? 'text-white/90' : 'text-slate-800'}`}>
                    {t('catalogs.recentDrafts')}
                  </span>
                </div>

                {safeDrafts.length === 0 ? (
                  <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'} py-1`}>
                    {t('catalogs.noDrafts')}
                  </p>
                ) : (
                  <div className="space-y-1">
                    {safeDrafts.slice(0, 3).map((draft) => (
                      <button
                        key={draft.id}
                        onClick={() => typeof onOpenDraft === 'function' && onOpenDraft(draft)}
                        className={`w-full text-left px-3 py-2.5 rounded-xl transition-all duration-150 group/draft ${isDarkMode
                          ? 'hover:bg-white/8 border border-transparent hover:border-white/10'
                          : 'hover:bg-slate-50 border border-transparent hover:border-slate-200'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm font-medium truncate ${isDarkMode ? 'text-white/80 group-hover/draft:text-white' : 'text-slate-700 group-hover/draft:text-slate-900'}`}>
                            {getDraftTitle(draft)}
                          </span>
                          <span className={`text-xs flex-shrink-0 px-1.5 py-0.5 rounded-md ${isDarkMode
                            ? 'bg-white/10 text-white/50'
                            : 'bg-slate-100 text-slate-400'
                          }`}>
                            {draft.status || t('pr.draftLabel')}
                          </span>
                        </div>
                        <div className={`flex items-center gap-3 mt-0.5 text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
                          <span>{t('catalogs.positionsCount', { count: getDraftPositions(draft) })}</span>
                          <span>·</span>
                          <span>{formatEur(getDraftValue(draft))}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* c) Hilfe & Support */}
            <div className={`rounded-2xl overflow-hidden ${isDarkMode
              ? 'bg-gray-900/40 border border-white/15 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]'
              : 'bg-slate-100 border border-slate-200'
            }`}>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-white/10' : 'bg-slate-200'}`}>
                    <HelpCircle className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`} />
                  </div>
                  <span className={`font-semibold text-sm ${isDarkMode ? 'text-white/90' : 'text-slate-700'}`}>
                    {t('catalogs.help')}
                  </span>
                </div>
                <p className={`text-xs leading-relaxed mb-3 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
                  {t('catalogs.helpDesc')}
                </p>
                <div className="space-y-2">
                  {/* Help Center existiert noch nicht — Button erst wieder rein, wenn es ein Ziel gibt
                      (kein toter Button; Taras-Befund 29.07. „Hilfe & Support noch inaktiv") */}
                  <a
                    href="mailto:info@gineex.de?subject=Gineex%20Support"
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium transition-all duration-150 ${isDarkMode
                      ? 'bg-white/8 hover:bg-white/12 text-white/70 border border-white/10'
                      : 'bg-white hover:bg-slate-50 text-slate-600 border border-slate-200'
                    }`}
                  >
                    <Headset className="w-3.5 h-3.5 flex-shrink-0" />
                    {t('catalogs.contactSupport')}
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default CatalogView;
